import { Box, Button, Typography, useTheme } from "@mui/material";
import { Plus } from "lucide-react";
import { type FC, useMemo } from "react";
import { useGoalDetailPortfoliosQuery } from "web_myplann/Api/Goals/useGoalDetailPortfoliosQuery";
import { PinExistingContractItem } from "web_myplann/Goals/Components/PinContract/PinExistingContractItem";
import type {
	GoalPortfolioListItemResponseDTO,
	GoalPortfolioResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

type Props = {
	open: boolean;
	onClose: () => void;
	onSelect: (portfolio: GoalPortfolioListItemResponseDTO) => void;
	onAddNew: () => void;
	goalPortfolios: GoalPortfolioResponseDTO[];
};

export const PinExistingContractDrawer: FC<Props> = ({
	open,
	onClose,
	onSelect,
	onAddNew,
	goalPortfolios,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const { data } = useGoalDetailPortfoliosQuery();

	const portfolios = useMemo(() => data?.data ?? [], [data]);
	const pinnedById = useMemo(
		() =>
			new Map(goalPortfolios.map((gp) => [gp.portfolioId, gp.usagePercent])),
		[goalPortfolios],
	);

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(Resources.Goals.Detail.Solution.Pin.Existing.title)}
			closeButton
		>
			<Box pb={2}>
				{portfolios.length === 0 ? (
					<Box px={2} py={3} textAlign="center">
						<Typography variant="body1" color={theme.palette.grey[500]}>
							{t(Resources.Goals.Detail.Solution.Pin.Existing.allPinned)}
						</Typography>
					</Box>
				) : (
					<>
						<Typography variant="hint" px={2} pb={1.4}>
							{t(Resources.Goals.Detail.Solution.Pin.Existing.hint)}
						</Typography>
						{portfolios.map((p, i) => (
							<PinExistingContractItem
								key={p.id}
								portfolio={p}
								pinnedAllocation={pinnedById.get(p.id) ?? null}
								hasDivider={i < portfolios.length - 1}
								onSelect={onSelect}
							/>
						))}
					</>
				)}
				<Box px={2} py={1} borderTop={`1px solid ${theme.palette.grey[100]}`}>
					<Button variant="inlineAction" disableRipple onClick={onAddNew}>
						<Plus size={14} />
						{t(Resources.Goals.Detail.Solution.Pin.Existing.addNew)}
					</Button>
				</Box>
			</Box>
		</MyPlannDrawer>
	);
};
